/**
 * Shared pieces of the firm-only working papers (V98-6, ADR-0047) — the
 * Track 6 review's cross-cutting fixes live here so every tab gets them:
 *
 *   T6-B4  one place handles `mfa_required` / `step_up_required`: unsaved form
 *          state is parked in sessionStorage, the person is asked to sign in
 *          again, and on return the drafts are restored and the interrupted
 *          write is retried once.
 *   T6-H1  buttons are never `disabled` while busy (focus stays put): they get
 *          `aria-disabled` and a click guard, with the reason linked by
 *          `aria-describedby`.
 *   T6-H2  one persistent polite status region; writes announce what happened.
 *   Medium status labels in sentence case from ONE map, one tone rule
 *          (accent = needs your action).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Spinner, type ButtonProps } from '@aigovops/design-system';
import { ApiError } from '../../lib/api';
import { firmApi, type FirmOverview, type FirmRole } from '../../lib/firm-api';
import { saveDraft, startStepUp, stepUpUrlOf, takeDraft } from '../../lib/firm-step-up';

export const errText = (e: unknown) => (e instanceof ApiError ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message) : e instanceof Error ? e.message : String(e));
export const errCode = (e: unknown) => (e instanceof ApiError ? ((e.body as { code?: string } | undefined)?.code ?? null) : null);

export const isLead = (r: FirmRole) => r === 'partner' || r === 'manager';
export const ROLE_LABEL: Record<FirmRole, string> = { partner: 'Partner', manager: 'Manager', senior: 'Senior', staff: 'Staff' };

// ---- labels: one map, sentence case, one tone rule ------------------------------------

type Tone = 'accent' | 'neutral' | 'danger' | 'success';
/** accent = needs YOUR action now; success = done; danger = a failure/lock; neutral = waiting on someone else. */
export const STATE: Record<string, { label: string; tone: Tone }> = {
  // PBC
  requested: { label: 'Requested', tone: 'neutral' },
  submitted: { label: 'Submitted · review', tone: 'accent' },
  returned: { label: 'Returned to client', tone: 'neutral' },
  accepted: { label: 'Accepted', tone: 'success' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
  // tests
  in_progress: { label: 'In progress', tone: 'neutral' },
  prepared: { label: 'Prepared · awaiting review', tone: 'accent' },
  reviewed: { label: 'Reviewed', tone: 'success' },
  // exceptions
  draft: { label: 'Draft · firm only', tone: 'neutral' },
  issued: { label: 'Issued', tone: 'neutral' },
  // documents
  superseded: { label: 'Superseded', tone: 'neutral' },
  // review notes
  open: { label: 'Open', tone: 'accent' },
  addressed: { label: 'Addressed', tone: 'accent' },
  cleared: { label: 'Cleared', tone: 'success' },
};
export function StateBadge({ state, label, tone }: { state: string; label?: string; tone?: Tone }) {
  const s = STATE[state] ?? { label: state.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), tone: 'neutral' as Tone };
  return <Chip tone={tone ?? s.tone}>{label ?? s.label}</Chip>;
}

/** A status chip in the firm palette (tokens only; firm.css). */
export function Chip({ tone = 'neutral', children, id }: { tone?: Tone | 'dashed'; children: ReactNode; id?: string }) {
  return <span id={id} className={`gp-firm-chip gp-firm-chip--${tone}`}>{children}</span>;
}

// ---- the engagement context ------------------------------------------------------------

/** A firmApi write that can be replayed after a step-up (serialisable). */
export type PendingCall = { method: keyof typeof firmApi; args: unknown[]; message: string };

interface FirmCtxValue {
  slug: string;
  eng: string;
  o: FirmOverview;
  lead: boolean;
  /** Fieldwork: everything writable. */
  writable: boolean;
  announce: (msg: string) => void;
  /** Ask for a fresh sign-in; parks drafts + the interrupted write (T6-B4). */
  requireStepUp: (url: string, pending?: PendingCall) => void;
  /** Forms register their live values so a step-up keeps them. */
  drafts: Map<string, unknown>;
  /** The id of the read-only line every locked control points at (T6-H3). */
  lockId: string | null;
}
const FirmCtx = createContext<FirmCtxValue | null>(null);
export const FirmProvider = FirmCtx.Provider;
export function useFirm(): FirmCtxValue {
  const v = useContext(FirmCtx);
  if (!v) throw new Error('useFirm outside the firm working papers');
  return v;
}

const PENDING_KEY = (eng: string) => `gp-firm-pending:${eng}`;
export function parkPending(eng: string, p: PendingCall | undefined): void {
  try {
    if (p) sessionStorage.setItem(PENDING_KEY(eng), JSON.stringify(p));
  } catch { /* storage off: the person re-does the action */ }
}
export function takePending(eng: string): PendingCall | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY(eng));
    sessionStorage.removeItem(PENDING_KEY(eng));
    const p = raw ? (JSON.parse(raw) as PendingCall) : null;
    return p && typeof p.method === 'string' && p.method in firmApi && Array.isArray(p.args) ? p : null;
  } catch {
    return null;
  }
}

/** Shell-side state for T6-B4: the step-up prompt and parking. */
export function useStepUpGate(eng: string) {
  const drafts = useRef(new Map<string, unknown>()).current;
  const [need, setNeed] = useState<string | null>(null);
  const pending = useRef<PendingCall | undefined>(undefined);
  const requireStepUp = useCallback((url: string, p?: PendingCall) => {
    if (p) pending.current = p;
    setNeed(url);
  }, []);
  const go = useCallback(() => {
    if (!need) return;
    saveDraft(eng, Object.fromEntries(drafts));
    parkPending(eng, pending.current);
    startStepUp(need);
  }, [need, eng, drafts]);
  return { drafts, need, requireStepUp, go };
}

/**
 * A form field whose value survives a step-up round trip (T6-B4): starts from
 * a parked draft when there is one, and registers itself so the shell can
 * park it before leaving.
 */
export function useDraft<T>(key: string, initial: T): [T, (v: T) => void] {
  const { eng, drafts } = useFirm();
  const [value, setValue] = useState<T>(() => {
    const parked = takeDraft<T>(eng, key);
    return parked === undefined ? initial : parked;
  });
  useEffect(() => {
    if (value === initial || value === '' || value === null) drafts.delete(key);
    else drafts.set(key, value);
    return () => { drafts.delete(key); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);
  return [value, setValue];
}

// ---- data hooks ------------------------------------------------------------------------

export interface Loaded<T> { data: T | null; error: string | null; raw: unknown; loading: boolean; reload: () => void }

/** Load + refresh one resource. A step-up answer goes to the shell's prompt (T6-B4). */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const ctx = useContext(FirmCtx);
  const [state, setState] = useState<{ data: T | null; error: string | null; raw: unknown; loading: boolean }>({ data: null, error: null, raw: null, loading: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    load().then(
      (data) => live && setState({ data, error: null, raw: null, loading: false }),
      (e) => {
        if (!live) return;
        const url = stepUpUrlOf(e);
        if (url && ctx) ctx.requireStepUp(url);
        setState((s) => ({ data: s.data, error: url ? null : errText(e), raw: e, loading: false }));
      },
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { ...state, reload: useCallback(() => setN((x) => x + 1), []) };
}

export interface Act {
  busy: boolean;
  error: string | null;
  code: string | null;
  clear: () => void;
  /**
   * Run a firmApi write (replayable after a step-up), refresh, announce
   * `message` on success. Resolves true on success. A second call while one
   * is in flight is ignored (the guard that replaces `disabled`, T6-H1).
   */
  call: <K extends keyof typeof firmApi>(method: K, args: Parameters<(typeof firmApi)[K]>, message: string) => Promise<boolean>;
  /** Any other async write (client-side APIs, downloads). */
  run: (fn: () => Promise<unknown>, message?: string) => Promise<boolean>;
}

export function useAct(after: () => void): Act {
  const ctx = useContext(FirmCtx);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; code: string | null } | null>(null);
  const inFlight = useRef(false);
  const exec = async (fn: () => Promise<unknown>, message: string | undefined, pending?: PendingCall) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await fn();
      after();
      if (message) ctx?.announce(message);
      return true;
    } catch (e) {
      const url = stepUpUrlOf(e);
      if (url && ctx) ctx.requireStepUp(url, pending);
      else setError({ text: errText(e), code: errCode(e) });
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return {
    busy,
    error: error?.text ?? null,
    code: error?.code ?? null,
    clear: () => setError(null),
    call: (method, args, message) => exec(
      () => (firmApi[method] as (...a: unknown[]) => Promise<unknown>)(...(args as unknown[])),
      message,
      { method, args: args as unknown[], message },
    ),
    run: (fn, message) => exec(fn, message),
  };
}

// ---- small components ------------------------------------------------------------------

export function Loading<T>({ l, children }: { l: { data: T | null; error: string | null; loading: boolean }; children: (d: T) => ReactNode }) {
  if (l.loading && !l.data) return <Spinner label="Loading" />;
  if (l.error) return <Alert variant="error">{l.error}</Alert>;
  if (!l.data) return null;
  return <>{children(l.data)}</>;
}

/**
 * T6-H1: a button that stays focusable when it can't act. `blocked` (or busy)
 * sets aria-disabled and swallows the click; `reason` is rendered next to it
 * and linked with aria-describedby (role clarity: "Manager or partner issues
 * exceptions"). The default variant is the outline style (T6-H12).
 */
export function GuardedButton({ blocked, busy, reason, reasonId, onClick, children, ...rest }: ButtonProps & { blocked?: boolean; busy?: boolean; reason?: ReactNode; reasonId?: string }) {
  const id = useRef(reasonId ?? `why-${Math.random().toString(36).slice(2, 9)}`).current;
  const off = !!blocked || !!busy;
  return (
    <>
      <Button
        {...rest}
        // T6-H12: an unavailable action reads as the outline style, never a filled primary.
        variant={blocked ? 'secondary' : rest.variant}
        aria-disabled={off || undefined}
        aria-describedby={[rest['aria-describedby'], blocked && reason ? id : null].filter(Boolean).join(' ') || undefined}
        onClick={(e) => {
          // Busy: swallow. Blocked submit: let the form's handler run so it
          // can show its field errors (T6-H14 — validate on submit).
          if (busy || (blocked && rest.type !== 'submit')) { e.preventDefault(); return; }
          onClick?.(e);
        }}
      >
        {children}
      </Button>
      {blocked && reason ? <span className="gp-firm-why" id={id}>{reason}</span> : null}
    </>
  );
}

/** The tab heading: h2, focusable so a change of view can move focus to it (T6-H1). */
export function TabHeading({ id, children, right }: { id: string; children: ReactNode; right?: ReactNode }) {
  return (
    <div className="gp-firm-tabhead">
      <h2 id={id} tabIndex={-1} className="gp-firm-h2">{children}</h2>
      {right}
    </div>
  );
}

/** Focus an element by id after the next render (never <body>). */
export function useFocusLater(): (id: string) => void {
  const want = useRef<string | null>(null);
  const [, bump] = useState(0);
  useEffect(() => {
    if (!want.current) return;
    const el = document.getElementById(want.current);
    if (el) {
      if (!el.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) el.setAttribute('tabindex', '-1');
      el.focus();
      want.current = null;
    }
  });
  return useCallback((id: string) => { want.current = id; bump((x) => x + 1); }, []);
}

/**
 * T6-H11: "Template in review" once per tab; the reviewer and the reference
 * live behind "Verify" — never the raw review string in the shell.
 */
export function TemplateNotice({ detail }: { detail: string | null | undefined }) {
  if (!detail) return null;
  return (
    <p className="gp-firm-notice">
      <Chip>Template in review</Chip>
      <details className="gp-firm-verify">
        <summary>Verify</summary>
        <span>The AICPA-structured template is awaiting editorial review against the illustrative wording.</span>
      </details>
    </p>
  );
}

/** T6-H11: an export's seal as a badge; what signs it sits behind "Verify". */
export function SealBadge({ label }: { label: string }) {
  return (
    <span className="gp-firm-seal">
      <Chip tone="success">Sealed</Chip>
      <details className="gp-firm-verify">
        <summary>Verify</summary>
        <span>{label}</span>
      </details>
    </span>
  );
}
